import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export function normalizePhoneDigits(raw: string | null | undefined): string {
	if (!raw) return '';
	let digits = raw.replace(/\D/g, '');
	if (digits.length === 11 && digits.startsWith('1')) {
		digits = digits.slice(1);
	}
	return digits;
}

/**
 * DOVE-2178 — blocks a phone number matching (all 10 normalized digits) an
 * existing phone on the patient. This is a UX shortcut only — the backend is
 * the actual source of truth and enforces the same rule independently.
 *
 * `getExistingPhones` is called fresh on every validation run (not
 * snapshotted at attach time), so the validator stays correct once the
 * async phone list finishes loading — no need to re-attach it later, though
 * callers should still call `updateValueAndValidity()` once the list arrives
 * so an already-typed value gets re-checked against it.
 *
 * `excludeId`, if provided, skips that phone from the comparison — used on
 * edit so a phone isn't flagged as a duplicate of itself when unchanged.
 */
export function duplicatePhoneNumberValidator(
	getExistingPhones: () => { id: number; number: string }[],
	excludeId?: number | null
): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const normalized = normalizePhoneDigits(control.value);
		if (normalized.length !== 10) {
			return null; // incomplete/invalid format — let other validators handle it
		}
		const isDuplicate = getExistingPhones().some(
			(phone) => phone.id !== excludeId && normalizePhoneDigits(phone.number) === normalized
		);
		return isDuplicate ? { duplicate: true } : null;
	};
}

export function formatPhoneDisplay(raw: string | null | undefined): string {
	const digits = normalizePhoneDigits(raw);
	if (digits.length !== 10) return raw ?? '';
	return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function formatPhoneTel(raw: string | null | undefined): string {
	const digits = normalizePhoneDigits(raw);
	if (digits.length !== 10) return '';
	return `+1${digits}`;
}
